import { useEffect, useRef, useState } from "react";

/**
 * Animated counter that starts when in view
 * Accepts { end, suffix, prefix, duration, label }
 */
export default function Counter({ end, suffix = "", prefix = "", duration = 1600, label }) {
    const [value, setValue] = useState(0);
    const [started, setStarted] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const io = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && !started) setStarted(true);
            },
            { threshold: 0.3 }
        );
        io.observe(el);
        return () => io.disconnect();
    }, [started]);

    useEffect(() => {
        if (!started) return;
        const t0 = performance.now();
        let raf;
        const step = (t) => {
            const p = Math.min(1, (t - t0) / duration);
            const eased = 1 - Math.pow(1 - p, 3);
            setValue(Math.round(end * eased));
            if (p < 1) raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf);
    }, [started, end, duration]);

    return (
        <div ref={ref} className="flex flex-col items-start gap-2" data-testid={`counter-${label?.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="font-heading text-5xl sm:text-6xl font-extrabold tabular-nums leading-none">
                {prefix}
                {value}
                {suffix}
            </div>
            <div className="text-sm text-muted-foreground uppercase tracking-[0.18em] font-semibold">{label}</div>
        </div>
    );
}
